import { type ReturnDate } from '@knack/asterisk-react/dist/src/components/date-time-picker/components/DateTimeInput/DateTimeInput';
import { type DateRangeWithErrors } from '@knack/asterisk-react/dist/src/components/date-time-picker/DateTimePicker';
import { DateTime } from 'luxon';

import { type DateTimeFieldFormat } from '@/types/schema/fields/DateTimeField';
import { getTimezoneConfig } from '@/utils/timezone';
import { getLuxonDateFormatFromFieldFormat } from '@/components/knack-date-picker/transformers/DateTimeFormatToLuxonFormat';
import {
  type DateTimeAttributes,
  type DateTimeRawValue
} from '@/components/knack-date-picker/types';

export function isDate(date: ReturnDate): date is Date {
  return date instanceof Date;
}

function JsDateSingleToKnackDateTransformer(
  date: ReturnDate,
  fieldFormat: DateTimeFieldFormat
): DateTimeRawValue {
  if (!isDate(date)) {
    return {
      date: '',
      hours: '',
      minutes: '',
      am_pm: '' as '' | 'AM' | 'PM' | undefined
    };
  }

  // IMPORTANT: We "break" the UTC time zone here to make sure the date is not converted to the local time zone
  // Return to normal when the time zone is fixed in the server
  const luxonDate = DateTime.fromJSDate(date).setZone(getTimezoneConfig().setZone, {
    keepLocalTime: true
  });
  const dateFormat = getLuxonDateFormatFromFieldFormat(fieldFormat.date_format);

  const formatTime = (): Pick<DateTimeAttributes, 'hours' | 'minutes' | 'am_pm'> => {
    const hours24 = luxonDate.toFormat('HH');
    const hours12 = hours24 === '00' ? '12' : hours24;
    const minutes = luxonDate.toFormat('mm');
    const amPm = (luxonDate.toFormat('a') as 'AM' | 'PM' | undefined) ?? '';

    return { hours: hours12, minutes, am_pm: amPm };
  };

  const formattedDate = dateFormat ? luxonDate.toFormat(dateFormat) : undefined;

  if (fieldFormat.time_format === 'Ignore Time') {
    return {
      date: formattedDate,
      hours: '',
      minutes: '',
      am_pm: '' as '' | 'AM' | 'PM' | undefined
    };
  }

  if (fieldFormat.date_format === 'Ignore Date') {
    return {
      date: '',
      ...formatTime()
    };
  }

  return {
    date: formattedDate,
    ...formatTime()
  };
}

export function JsDateRangeToKnackDateTransformer(
  date: DateRangeWithErrors | undefined,
  fieldSettings: DateTimeFieldFormat
): DateTimeRawValue {
  return {
    ...JsDateSingleToKnackDateTransformer(date?.from || undefined, fieldSettings),
    to: JsDateSingleToKnackDateTransformer(date?.to || undefined, fieldSettings)
  };
}

function isDateRangeWithErrorsGuard(
  date: DateRangeWithErrors | ReturnDate
): date is DateRangeWithErrors {
  return !!date && (date as DateRangeWithErrors).from !== undefined;
}

export function JsDateToKnackDateTransformer(
  date: DateRangeWithErrors | ReturnDate,
  fieldSettings: DateTimeFieldFormat
): DateTimeRawValue {
  if (isDateRangeWithErrorsGuard(date)) {
    return JsDateRangeToKnackDateTransformer(date, fieldSettings);
  }

  return JsDateSingleToKnackDateTransformer(date, fieldSettings);
}
